import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback agent roster in case API fails
const FALLBACK = [
  { id: '1', name: '爆款文案生成器', icon: '✍️', description: '根據您的帳號定位和目標受眾，生成高質量的 Instagram 內容選題和腳本。', category: 'copywriting' },
  { id: '2', name: '故事生成器',   icon: '📖', description: '透過問答，為你的個人品牌量身打造一個情緒飽滿、有記憶點的創始故事。', category: 'storytelling' },
  { id: '3', name: '爆款影片分析師', icon: '🎬', description: '貼上任何爆款 Reels 連結，AI 幫你拆解結構、Hook 技法與情緒曲線。', category: 'analysis' },
];

const CATEGORY_META = {
  copywriting: { label: 'COPY', accent: '#FF5436' },
  storytelling: { label: 'STORY', accent: '#A3E635' },
  analysis:     { label: 'ANALYZE', accent: '#60A5FA' },
  default:      { label: 'AGENT', accent: '#FF5436' },
};

const AgentCard = ({ agent, index }) => {
  const meta = CATEGORY_META[agent.category] || CATEGORY_META.default;
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="group relative bg-obsidian-900 border border-white/[0.08] rounded-2xl p-6 md:p-7 overflow-hidden hover:border-lime-500/30 transition-all duration-500"
      data-testid={`agent-card-${agent.id}`}
    >
      {/* Hover radial accent */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 30% 10%, ${meta.accent}14, transparent 60%)`,
        }}
      />

      {/* Serif initial watermark */}
      <div
        aria-hidden
        className="absolute -top-6 -right-4 font-serif font-bold text-white/[0.04] leading-none pointer-events-none select-none group-hover:text-white/[0.07] transition-colors duration-500"
        style={{ fontSize: '160px' }}
      >
        {agent.name?.[0] || '·'}
      </div>

      <div className="relative">
        {/* Top row */}
        <div className="flex items-start justify-between mb-6">
          <div
            className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-500"
            style={{ boxShadow: `0 0 0 0 ${meta.accent}00` }}
          >
            {agent.icon || '🤖'}
          </div>
          <span
            className="label-micro px-2 py-1 rounded-full border border-white/10 text-obsidian-300"
            style={{ color: meta.accent, borderColor: `${meta.accent}33` }}
          >
            {meta.label}
          </span>
        </div>

        <h3 className="font-display font-semibold text-white text-xl md:text-2xl mb-3 tracking-tight">
          {agent.name}
        </h3>
        <p className="text-obsidian-200 text-sm leading-relaxed mb-6 line-clamp-3 min-h-[3.75rem]">
          {agent.description || '專為華語創作者打造的智能助理。'}
        </p>

        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-1.5 text-sm text-obsidian-300 group-hover:text-lime-500 transition-colors"
          data-testid={`agent-try-btn-${agent.id}`}
        >
          <span>試用此代理</span>
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </button>
      </div>

      {/* Corner accent line */}
      <div
        className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-700"
        style={{ background: `linear-gradient(90deg, ${meta.accent}, transparent)` }}
      />
    </motion.div>
  );
};

const AgentRoster = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [agents, setAgents] = useState(FALLBACK);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/showcase/agents`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setAgents(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      ref={ref}
      id="agents"
      className="relative py-24 md:py-32"
      data-testid="agent-roster-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-lime-500/60" />
              <span className="label-micro text-lime-500">
                AGENTS · 0{agents.length}
              </span>
            </div>
            <h2 className="font-display font-semibold text-white tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              認識你的<span className="text-lime-500"> AI 代理團</span>
            </h2>
            <p className="text-obsidian-200 text-base md:text-lg mt-5">
              每一位代理都有自己的專精領域、訓練數據與提示工程。選對代理，腳本品質立刻升級。
            </p>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {agents.map((a, i) => (
            <AgentCard key={a.id || i} agent={a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AgentRoster;
